<script lang="ts">
  import {onDestroy, onMount} from 'svelte';
  import type {Video} from '@/types/project';
  import {preview, thumbnail, user} from '@/store';
  import {Icon, PreviewStyles} from '@components';
  import {getInstance} from '@/services/animation';
  import {VideoService} from '@/services';
  import {toTitleCase, wait} from '../../utils/common';
  import type {TemplateInformation} from '../../types/preview';
  import previewAreaLoadingStore from './loading-store';
  import editorInstance from '../../lib/editor/editor';
  import Swal from 'sweetalert2';
  import editorStore from '../../lib/editor/editor.store';
  import PopupBox from '../../components/thumbnail-sidebar/PopupBox/PopupBox.svelte';
  import {videoScale} from '@/store/scale';
  import {logError} from '../../utils/logs';
  import AnimationController from '../../components/animations/AnimationController.svelte';
  import {httpClient} from '@/utils/http-client';
  import viddialog from './viddialog-store';

  let container: HTMLElement;
  let observer: MutationObserver;
  let video: Video;
  let animationService = getInstance();
  const videoService = new VideoService();
  let loadingProgress = 0;
  let status = 'Compiling video... this typically takes less than a minute';
  let trackChanges = false;

  const displayNetworkError = () => {
    Swal.fire({
      title: 'There appears to have been a problem.',
      text: "The system was unable to compile your video. This is usually because the source video is too large or the quality is too high. We are working on solutions to mitigate the issue. In the mean time, if you are able to upload a smaller or lower quality video, that may help. If the issue persists or you don't believe this to be the case, please contact customer support immediately and our team will be ready to assist you.",
      icon: 'error'
    });
  };

  const delay = async (time: number) => {
    return new Promise(resolve => setTimeout(resolve, time));
  };

  const loadVideo = async (tries: number) => {
    if (tries == 5) {
      displayNetworkError();
      throw new Error('Could not load video.');
    }
    try {
      if (!$thumbnail.video) throw new Error('Project Video is undefined.');
      video = await videoService.getVideo($thumbnail.video.id);
      thumbnail.set({
        isUpdated: false,
        isNew: false
      });
      // createPreviewDom($thumbnail.template, $thumbnail);
    } catch (error) {
      logError('Unable to load video', error);
      await delay(1000);
      loadVideo(tries + 1);
    }
  };

  const quote = $thumbnail.quote;
  const title = $thumbnail.title;
  const type = $thumbnail.type;
  const snapshot = $thumbnail.snapshot;
  const createPreviewDom = async (template: any, thumbnail: any) => {
    const node = animationService.getDom();
    node.id = 'outerHTML';
    node.style.opacity = '0';
    node.style.height = '100%';
    if (!container)
      return console.error('CONTAINER ELEMENT NOT FOUND', {
        container,
        node,
        thumbnail
      });

    container.innerHTML = '';
    container.append(node);
    node.addEventListener('click', e => {
      const target: HTMLElement = e.target as HTMLElement;
      if (!(target === node)) return;
      editorInstance.disableFocusMode();
      editorStore.setActiveComponent(null);
    });
    animationService.reset().onCompleteTemplateBuild(() => {
      previewAreaLoadingStore.set(false);
    });
    await animationService.buildTemplate(template);
    await wait(1000);
    trackChanges = true;
    node.style.opacity = '1';
    editorInstance.initialize(animationService.getDom());
    if (type === 'quote' && quote) insertQuote(quote);
    else if (type === 'thumbnail' && title) insertTitle(title);
    animationService.templateLoader.initializeLoaders();
    const ignored = ['sermonclip', 'caption', 'logo', 'sermon title', 'quote', 'title', 'pastorname'];

    let extraInformation: Array<TemplateInformation> = [];

    node.querySelectorAll('[data-amelement]');

    const elements = document.querySelectorAll('[data-amelement]');

    // Check if any element has the attribute value 'thumbnail'
    const hasThumbnail = Array.from(elements).some(element => element.getAttribute('data-amelement') === 'thumbnail');

    if (!hasThumbnail) {
      viddialog.set(false);
    } else if (hasThumbnail && !snapshot) viddialog.set(true);

    node.querySelectorAll('[data-amelement]').forEach(element => {
      const attribute = element.getAttribute('data-amelement') as string;

      if (attribute === 'sermonclip') {
        const div = element as HTMLDivElement;

        sermonclipHeight = div.clientHeight;
        sermonclipWidth = div.clientWidth;

        videoScale.set({
          videoHeight: sermonclipHeight,
          videoWidth: sermonclipWidth,
          scale: sermonclipScale
        });
      }

      if (attribute && ignored.includes(attribute)) return false; // Skip this ignored case
      const type = element.getAttribute('data-amtype') as string;
      const value = element.textContent?.replace(/[\n\r]+|[\s]{2,}/g, ' ').trim() || ('' as string);
      extraInformation.push({
        key: attribute,
        label: toTitleCase(attribute),
        value,
        type
      });
    });

    animationService.rebuildChanges();

    preview.set({
      checkIfSermonTitleExist: animationService.checkForSermonTitle(),
      checkIfBrandLogoExists: animationService.checkForBrandLogo(),
      music: animationService.templateMusic,
      extraInformation
    });

    // Start observing the target node for configured mutations
    observer.observe(node, {attributes: true, childList: true, subtree: true, characterData: true});
  };
  function insertQuote(text: string) {
    let quote = text;
    editorInstance.history.addHistory('Insert Quote/Title');
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      quote = doc.body.textContent || '';
    } catch (error) {
      console.error(error);
    }
    editorInstance.insertQuote(quote);
    thumbnail.set({...$thumbnail, quote});
  }

  function insertTitle(text: string) {
    editorInstance.history.addHistory('Insert Quote/Title');
    editorInstance.insertTitle(text);
    thumbnail.set({...$thumbnail, title: text});
  }

  const getQuotes = async (tries: number) => {
    Swal.fire({
      position: 'bottom-end',
      icon: 'info',
      title: 'Fetching quotes...',
      showConfirmButton: false,
      toast: true
    });
    if ($thumbnail.video)
      httpClient
        .get(`/sermon-summary/${$thumbnail.video.id}/quotes`)
        .then(response => {
          try {
            insertQuote(response.data.quotes[0]);
            Swal.fire({
              position: 'bottom-end',
              icon: 'success',
              title: 'Quote inserted!',
              showConfirmButton: false,
              timer: 1500,
              toast: true
            });
          } catch (error) {
            Swal.fire({
              position: 'bottom-end',
              icon: 'info',
              title: `Fetching quotes trial ${tries + 2}...`,
              showConfirmButton: false,
              timer: 1500,
              toast: true
            });
            if (tries < 3) getQuotes(tries + 1);
          }
        })
        .catch(error => {
          Swal.fire({
            position: 'bottom-end',
            icon: 'error',
            title: 'Error fetching quotes!',
            showConfirmButton: false,
            timer: 1500,
            toast: true
          });
          if (tries < 3) getQuotes(tries + 1);
        });
  };

  const getTitles = async (tries: number) => {
    Swal.fire({
      position: 'bottom-end',
      icon: 'info',
      title: 'Fetching titles...',
      showConfirmButton: false,
      toast: true
    });
    if ($thumbnail.video)
      httpClient
        .get(`/sermon-summary/${$thumbnail.video.id}/titles`)
        .then(response => {
          try {
            insertTitle(response.data.titles[0]);
            Swal.fire({
              position: 'bottom-end',
              icon: 'success',
              title: 'Title inserted!',
              showConfirmButton: false,
              timer: 1500,
              toast: true
            });
          } catch (error) {
            Swal.fire({
              position: 'bottom-end',
              icon: 'info',
              title: `Fetching titles trial ${tries + 2}...`,
              showConfirmButton: false,
              timer: 1500,
              toast: true
            });
            if (tries < 3) getTitles(tries + 1);
          }
        })
        .catch(error => {
          Swal.fire({
            position: 'bottom-end',
            icon: 'error',
            title: 'Error fetching titles!',
            showConfirmButton: false,
            timer: 1500,
            toast: true
          });
          if (tries < 3) getTitles(tries + 1);
        });
  };

  function watchImage(mutationsList: any) {
    for (var mutation of mutationsList) {
      // This will trigger the DraftModule to update
      thumbnail.setOriginalTemplateHTML(animationService.getDom()?.innerHTML!);
    }
  }

  onMount(async () => {
    viddialog.set(false);
    const template = $thumbnail.originalTemplateHTML;
    previewAreaLoadingStore.set(true);
    if (!template) throw 'Undefined Video/Template';
    // await loadVideo(1);
    if ($thumbnail) createPreviewDom(template, $thumbnail);
    console.log($thumbnail.snapshot);
    if (!$preview.restored) {
      if ($thumbnail.type === 'quote' && !$thumbnail.quote) getQuotes(0);
      else if ($thumbnail.type === 'thumbnail' && !$thumbnail.title) getTitles(0);
    } else if ($thumbnail.snapshot) {
      editorInstance.insertPastorPic($thumbnail.snapshot);
      viddialog.set(false);
    }

    observer = new MutationObserver(watchImage);
  });

  let sermonclipHeight = 0;
  let sermonclipWidth = 0;
  let sermonclipScale = 0;
  let containerScale = 1;

  const unsubscribe = preview.subscribe(async store => {
    animationService.setMetaData({...store, brandKit: $user.brandKit});
    if (!trackChanges) return;
    animationService.buildMetadataDisplay();
  });

  let padding = 'padding-left: 0px;';

  $: loadingText = loadingProgress > 0 ? loadingProgress.toFixed(0) + '%' : status;

  onDestroy(() => {
    console.warn('Unloading editor instance...');
    editorInstance.unload();
    unsubscribe();
    observer.disconnect();
  });
</script>

<div class="relative flex h-full w-full flex-grow flex-col overflow-auto">
  <AnimationController />
  <div class="my-auto block h-full flex-grow overflow-hidden p-5" style={padding}>
    <div class="my-auto h-full flex-grow" style="transform: scale({containerScale * 100}%);">
      <div id="containerArea" class="my-auto h-full flex-grow px-8" bind:this={container}>
        {#if previewAreaLoadingStore}
          <div
            class=" flex h-full w-full flex-col items-center justify-center bg-gray-100 text-center align-middle"
            style="z-index:200;"
          >
            <Icon name="Spinner" className=" text-gray-400 w-12 h-12 animate-spin" />
            <div class="mt-2 block text-sm text-gray-500">
              {loadingText}
            </div>
          </div>
        {/if}
      </div>
    </div>
  </div>
</div>

<PreviewStyles />

<style lang="postcss">
  div#containerArea {
    /* position: absolute; */

    width: 100%;
    /* height: 80%; */
  }

  div#containerArea > div > div {
    @apply p-4;
  }
  input {
    -webkit-appearance: none;
    background-size: 0% 100%;
    background-repeat: no-repeat;
    @apply h-1 w-full rounded bg-gray-300 bg-gradient-to-r from-secondary to-secondary-600;
  }

  input::-webkit-slider-thumb {
    -webkit-appearance: none;
    @apply h-4 w-4 rounded-full bg-secondary shadow-md;
  }

  input::-webkit-slider-runnable-track {
    -webkit-appearance: none;
    @apply bg-transparent;
  }
</style>
